import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ThemeId = "alpe" | "breakaway" | "peloton";

export type Theme = {
  id: ThemeId;
  name: string;
  /** Full hex for JS inline styles */
  primaryHex: string;
  secondaryHex: string;
  /** "r g b" space-separated — used as CSS custom property value */
  primaryRgb: string;
  secondaryRgb: string;
};

export const THEMES: Theme[] = [
  {
    id: "alpe",
    name: "Alpe",
    // Deep slate blue — same muted, earthy tone as Peloton teal
    primaryHex: "#1e5a8a", // muted steel blue — ~5.5:1 on cream
    secondaryHex: "#174b75",
    primaryRgb: "30 90 138",
    secondaryRgb: "23 75 117",
  },
  {
    id: "breakaway",
    name: "Breakaway",
    // Warm terracotta — same depth/saturation as Peloton teal
    primaryHex: "#9a4a2f", // muted burnt clay — ~5.8:1 on cream
    secondaryHex: "#7f3d27",
    primaryRgb: "154 74 47",
    secondaryRgb: "127 61 39",
  },
  {
    id: "peloton",
    name: "Peloton",
    // Teal — precision, endurance, aerodynamic efficiency
    primaryHex: "#0f766e", // teal-700 — ~5.4:1 on warm cream
    secondaryHex: "#0f766e",
    primaryRgb: "15 118 110",
    secondaryRgb: "15 118 110",
  },
];

function applyThemeToDom(theme: Theme): void {
  document.documentElement.style.setProperty("--cp", theme.primaryRgb);
  document.documentElement.style.setProperty("--cs", theme.secondaryRgb);
  document.documentElement.setAttribute("data-mode", "light");
}

type ThemeStore = {
  themeId: ThemeId;
  setTheme: (id: ThemeId) => void;
  getActiveTheme: () => Theme;
};

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      themeId: "alpe",

      setTheme: (id) => {
        const theme = THEMES.find((t) => t.id === id) ?? THEMES[0];
        applyThemeToDom(theme);
        set({ themeId: id });
      },

      getActiveTheme: () => {
        return THEMES.find((t) => t.id === get().themeId) ?? THEMES[0];
      },
    }),
    {
      name: "cycling-theme",
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const theme = THEMES.find((t) => t.id === state.themeId) ?? THEMES[0];
        applyThemeToDom(theme);
      },
    },
  ),
);
